import * as React from 'react';

// API
import apibridge from '../apibridge';

// UTILS
import { validationErrorsToString } from '../utils';

// REDUX
import { useAppSelector } from '../store';

// PACKAGES
import { Link } from 'react-router-dom';

// COMPONENTS
import Button from './Button';
import DisplayDate from './DisplayDate';
import CircleImage from '../components/CircleImage';
import ErrorMessage from './ErrorMessage';

// ICONS
import { ReactComponent as PlusIcon } from '../assets/svg/plus.svg';

// TYPES
import { FamiliesFetchLinkedAccount } from '../api/models/families-fetch-linked-account';
import { DomainProfileType } from '../api/models';

interface ProfileLinkedAccountsTypes {
	linkedAccounts?: Array<FamiliesFetchLinkedAccount>;
	defaultMaxItems?: number;
	setClicked?: React.Dispatch<React.SetStateAction<boolean>>;
	setLinkedAccountsUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
	userId?: string;
	profileType?: DomainProfileType;
}

const ProfileLinkedAccounts: React.FC<ProfileLinkedAccountsTypes> = ({
	linkedAccounts,
	defaultMaxItems,
	setClicked,
	setLinkedAccountsUpdated,
	userId,
	profileType
}) => {
	const [maxLinkedAccountsItem, setMaxLinkedAccountsItem] = React.useState<number>(defaultMaxItems || 5);

	const [showError, setShowError] = React.useState<boolean>(false);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();

	const adminUserId = useAppSelector((state) => state.user.id);

	const isYou = userId === adminUserId;

	const isParent = profileType === DomainProfileType.Parent;
	const isStudent = profileType === DomainProfileType.Student;

	const revokeInvite = async (id: string) => {
		const data = await apibridge.postAccountLinkRequestRevoke({ id });

		if (data.isError) {
			setShowError(true);
			setFormErrorMessage(
				data.validationErrors.length
					? validationErrorsToString(data.validationErrors)
					: data.message || 'An error has occurred'
			);
		} else {
			setShowError(false);
			setLinkedAccountsUpdated && setLinkedAccountsUpdated(true);
		}
	};

	const removeLinkedAccount = async (id: string) => {
		const data = await apibridge.postAccountLinkRequestRemove({ id });

		if (data.isError) {
			setShowError(true);
			setFormErrorMessage(
				data.validationErrors.length
					? validationErrorsToString(data.validationErrors)
					: data.message || 'An error has occurred'
			);
		} else {
			setShowError(false);
			setLinkedAccountsUpdated && setLinkedAccountsUpdated(true);
		}
	};

	return (
		<>
			<section className="info-section">
				{((linkedAccounts && linkedAccounts.length > 0) || isYou) && (
					<div className="section-header">
						{linkedAccounts && linkedAccounts.length > 0 && <h2 className="header-name color-dark">Linked Accounts</h2>}

						{isYou && isParent && (
							<Button
								onClick={() => {
									setClicked!(true);
								}}
								className="btn-text-icon d-none d-lg-block"
								buttonIcon={<PlusIcon className="link-icon" />}
							>
								Invite to account
							</Button>
						)}
					</div>
				)}

				{linkedAccounts &&
					linkedAccounts.length > 0 &&
					linkedAccounts.slice(0, maxLinkedAccountsItem).map((item, index) => {
						return (
							<div key={index} className="row-container">
								<div className="row detail-row">
									<div className="col-auto pdl-0 pdr-0">
										<CircleImage src={item.profileImage} />
									</div>

									<div className="col-md-auto full-width">
										<div className="row detail-info">
											<div className="col-lg-4 detail-name">{item.name}</div>

											<div className="col-lg-4 detail-status">
												<div className="title">Relationship</div>
												<div className="name">{item.relationship}</div>
											</div>

											<div className="col-lg-4 detail-status">
												{item.isPending ? (
													<>
														<div className="title">Linked</div>

														<div className="status">Pending invitation</div>
													</>
												) : (
													<>
														{item.linked && (
															<>
																<div className="title">Linked</div>

																<div className="status">
																	<time>
																		<DisplayDate>{item.linked}</DisplayDate>
																	</time>
																</div>
															</>
														)}
													</>
												)}
											</div>
										</div>
									</div>

									<div className="col-auto pdr-0 link-group multiple">
										{isYou &&
											isParent &&
											(item.isPending ? (
												<Button
													type="button"
													onClick={() => item && item.id && revokeInvite(item.id)}
													className="btn-text pdr-0"
												>
													Revoke Request
												</Button>
											) : (
												<>
													<Button
														type="button"
														onClick={() => item && item.id && removeLinkedAccount(item.id)}
														className="btn-text remove"
													>
														Remove
													</Button>
												</>
											))}
									</div>
								</div>
							</div>
						);
					})}

				{showError && <ErrorMessage message={formErrorMessage} />}

				{linkedAccounts && maxLinkedAccountsItem < linkedAccounts!.length && (
					<Button
						className="btn-secondary btn-show-more"
						onClick={() => setMaxLinkedAccountsItem(linkedAccounts!.length)}
						buttonIcon={<PlusIcon className="link-icon" />}
					>
						Show {linkedAccounts!.length - maxLinkedAccountsItem} more
					</Button>
				)}
			</section>
		</>
	);
};

export default ProfileLinkedAccounts;
